const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

let authToken = null;

export const setAuthToken = (token) => {
  authToken = token;
  if (token) {
    localStorage.setItem('authToken', token);
  } else {
    localStorage.removeItem('authToken');
  }
};

const getHeaders = () => ({
  'Content-Type': 'application/json',
  ...(authToken && { 'Authorization': `Bearer ${authToken}` })
});

// Authentification
export const login = async (username, password) => {
  try {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    const data = await response.json();
    if (data.success) {
      setAuthToken(data.token);
    }
    return data;
  } catch (error) {
    return { success: false, message: 'Erreur de connexion au serveur' };
  }
};

export const verifyToken = async () => {
  try {
    const response = await fetch(`${API_URL}/auth/verify`, {
      method: 'POST',
      headers: getHeaders()
    });
    return await response.json();
  } catch (error) {
    return { success: false };
  }
};

// Commandes
export const getOrders = async () => {
  try {
    const response = await fetch(`${API_URL}/orders`, {
      headers: getHeaders()
    });
    return await response.json();
  } catch (error) {
    return { success: false, message: 'Erreur lors du chargement des commandes' };
  }
};

export const getOrder = async (orderId) => {
  try {
    const response = await fetch(`${API_URL}/orders/${orderId}`, {
      headers: getHeaders()
    });
    return await response.json();
  } catch (error) {
    return { success: false, message: 'Erreur lors du chargement de la commande' };
  }
};

export const createOrder = async (orderData) => {
  try {
    const response = await fetch(`${API_URL}/orders`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(orderData)
    });
    return await response.json();
  } catch (error) {
    return { success: false, message: 'Erreur lors de la création de la commande' };
  }
};

export const updateOrderStatus = async (orderId, status) => {
  try {
    const response = await fetch(`${API_URL}/orders/${orderId}/status`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify({ status })
    });
    return await response.json();
  } catch (error) {
    return { success: false, message: 'Erreur lors de la mise à jour du statut' };
  }
};

export const updateOrder = async (orderId, orderData) => {
  try {
    const response = await fetch(`${API_URL}/orders/${orderId}`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(orderData)
    });
    return await response.json();
  } catch (error) {
    return { success: false, message: 'Erreur lors de la mise à jour de la commande' };
  }
};

export const getOrderHistory = async (orderId) => {
  try {
    const response = await fetch(`${API_URL}/orders/${orderId}/history`, {
      headers: getHeaders()
    });
    return await response.json();
  } catch (error) {
    return { success: false, message: 'Erreur lors du chargement de l\'historique' };
  }
};

export const deleteOrder = async (orderId) => {
  try {
    const response = await fetch(`${API_URL}/orders/${orderId}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    return await response.json();
  } catch (error) {
    return { success: false, message: 'Erreur lors de la suppression de la commande' };
  }
};

export const getStats = async () => {
  try {
    const response = await fetch(`${API_URL}/stats`, {
      headers: getHeaders()
    });
    return await response.json();
  } catch (error) {
    return { success: false, message: 'Erreur lors du chargement des statistiques' };
  }
};

// ===== ADMIN APIs =====

export const getPastries = async () => {
  try {
    const response = await fetch(`${API_URL}/pastries`, {
      headers: getHeaders()
    });
    return await response.json();
  } catch (error) {
    return { success: false, message: 'Erreur lors du chargement des pâtisseries' };
  }
};

export const createPastry = async (name, description) => {
  try {
    const response = await fetch(`${API_URL}/pastries`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ name, description })
    });
    return await response.json();
  } catch (error) {
    return { success: false, message: 'Erreur lors de la création' };
  }
};

export const updatePastry = async (id, name, description) => {
  try {
    const response = await fetch(`${API_URL}/pastries/${id}`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify({ name, description })
    });
    return await response.json();
  } catch (error) {
    return { success: false, message: 'Erreur lors de la mise à jour' };
  }
};

export const deletePastry = async (id) => {
  try {
    const response = await fetch(`${API_URL}/pastries/${id}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    return await response.json();
  } catch (error) {
    return { success: false, message: 'Erreur lors de la suppression' };
  }
};

export const getAdminOrders = async () => {
  try {
    const response = await fetch(`${API_URL}/admin/orders`, {
      headers: getHeaders()
    });
    return await response.json();
  } catch (error) {
    return { success: false, message: 'Erreur lors du chargement des commandes' };
  }
};

// ===== USER MANAGEMENT APIs =====

export const getUsers = async () => {
  try {
    const response = await fetch(`${API_URL}/admin/users`, {
      headers: getHeaders()
    });
    return await response.json();
  } catch (error) {
    return { success: false, message: 'Erreur lors du chargement des utilisateurs' };
  }
};

export const createUser = async (username, password, role, name) => {
  try {
    const response = await fetch(`${API_URL}/admin/users`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ username, password, role, name })
    });
    return await response.json();
  } catch (error) {
    return { success: false, message: 'Erreur lors de la création de l\'utilisateur' };
  }
};

export const updateUser = async (id, role, name) => {
  try {
    const response = await fetch(`${API_URL}/admin/users/${id}`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify({ role, name })
    });
    return await response.json();
  } catch (error) {
    return { success: false, message: 'Erreur lors de la mise à jour de l\'utilisateur' };
  }
};

export const deleteUser = async (id) => {
  try {
    const response = await fetch(`${API_URL}/admin/users/${id}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    return await response.json();
  } catch (error) {
    return { success: false, message: 'Erreur lors de la suppression de l\'utilisateur' };
  }
};

export const resetUserPassword = async (id, newPassword) => {
  try {
    const response = await fetch(`${API_URL}/admin/users/${id}/reset-password`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ newPassword })
    });
    return await response.json();
  } catch (error) {
    return { success: false, message: 'Erreur lors de la réinitialisation du mot de passe' };
  }
};

// ===== LOGIN LOGS APIs =====

export const getLoginLogs = async (limit = 100) => {
  try {
    const response = await fetch(`${API_URL}/admin/login-logs?limit=${limit}`, {
      headers: getHeaders()
    });
    return await response.json();
  } catch (error) {
    return { success: false, message: 'Erreur lors du chargement des journaux de connexion' };
  }
};

export const initAuth = () => {
  const token = localStorage.getItem('authToken');
  if (token) {
    setAuthToken(token);
  }
};
